import * as React from 'react';
import { Responsive } from '@cian/ui-kit';
import { clsx } from 'clsx';
import { throttle } from 'throttle-debounce';

import * as styles from './styles.css';
import { getElementRect, getRightScrollOffset, getLeftScrollOffset, scrollToPos } from './utils';
import { SCROLL_THROTTLING_DELAY, SCROLL_CLICK_DEAD_ZONE } from '../../constants/verticalSlider';

export interface IVerticalSliderProps {
  /**
   * Отступ между элементами. По умолчанию 8px.
   */
  itemGap?: '8px' | '12px' | '16px' | '24px';
  /**
   * Если указано, то пытается показать на любых десктопных разрешениях указанное количество элементов.
   */
  itemsPerDesktopScreen?: number;
  leftArrow: React.FC<{ onClick(): void; topOffset?: number }>;
  rightArrow: React.FC<{ onClick(): void; topOffset?: number }>;
  /**
   * Отступ стрелочки сверху. По умолчанию по центру.
   */
  arrowTopOffset?: number;
  scrollOffset?: number;
  onItemClick?(index: number): void;
}

export type TVerticalSliderProps = React.PropsWithChildren<IVerticalSliderProps>;

interface IVerticalSliderState {
  leftArrowVisible: boolean;
  rightArrowVisible: boolean;
}

export class VerticalSlider extends React.Component<TVerticalSliderProps, IVerticalSliderState> {
  private scrollRef = React.createRef<HTMLUListElement>();
  private startX: number;
  private scrollLeft: number;
  private isDragging = false;

  public state = {
    leftArrowVisible: false,
    rightArrowVisible: false,
  };

  public componentDidMount() {
    this.changeControlsVisibility();

    window.addEventListener('resize', this.changeControlsVisibility);
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.changeControlsVisibility);
  }

  public render() {
    const { leftArrowVisible, rightArrowVisible } = this.state;
    const {
      arrowTopOffset,
      itemGap = '8px',
      itemsPerDesktopScreen,
      leftArrow: LeftArrow,
      rightArrow: RightArrow,
    } = this.props;

    const style = {
      '--item-gap': itemGap,
      '--items-per-desktop-screen': itemsPerDesktopScreen,
    } as React.CSSProperties;

    return (
      <div className={styles['wrapper']}>
        <ul
          className={clsx(styles['container'], itemsPerDesktopScreen && styles['items-per-screen-fixed'])}
          style={style}
          ref={this.scrollRef}
          onMouseDown={this.handleMouseDown}
          onScroll={this.throttledHandleScroll}
          data-testid="VerticalSliderList"
        >
          {React.Children.map(
            this.props.children,
            (child, i) =>
              child && (
                <li key={i} className={styles['item']} onClick={this.handleItemClick(i)}>
                  {child}
                </li>
              ),
          )}
        </ul>
        <Responsive desktop>
          <div className={styles['controls']}>
            {leftArrowVisible && (
              <div className={clsx(styles['arrow'], styles['left-arrow'])}>
                <LeftArrow onClick={this.onLeftArrowClick} topOffset={arrowTopOffset} />
              </div>
            )}
            {rightArrowVisible && (
              <div className={clsx(styles['arrow'], styles['right-arrow'])}>
                <RightArrow onClick={this.onRightArrowClick} topOffset={arrowTopOffset} />
              </div>
            )}
          </div>
        </Responsive>
      </div>
    );
  }

  private handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();

    const container = this.scrollRef.current;

    /* istanbul ignore else */
    if (container) {
      const containerRect = getElementRect(container);
      this.startX = e.pageX - containerRect.left;
      this.scrollLeft = container.scrollLeft;

      window.addEventListener('mousemove', this.handleMove);
      window.addEventListener('mouseup', this.handleMoveEnd);
    }

    this.isDragging = false;
  };

  private handleMoveEnd = () => {
    window.removeEventListener('mousemove', this.handleMove);
    window.removeEventListener('mousemove', this.handleMoveEnd);
  };

  private handleItemClick = (i: number) => (e: React.MouseEvent) => {
    if (this.isDragging) {
      e.stopPropagation();

      return;
    }

    const { onItemClick } = this.props;

    /* istanbul ignore else */
    if (onItemClick) {
      onItemClick(i);
    }
  };

  private handleMove = (e: MouseEvent) => {
    const container = this.scrollRef.current;

    /* istanbul ignore else */
    if (container) {
      const containerRect = getElementRect(container);
      if (Math.abs(e.pageX - this.startX) >= SCROLL_CLICK_DEAD_ZONE) {
        this.isDragging = true;
      }

      const x = e.pageX - containerRect.left;
      const walk = x - this.startX;

      container.scrollLeft = this.scrollLeft - walk;

      /* istanbul ignore else */
      if (container.scrollLeft <= 0) {
        this.startX = x;
        this.scrollLeft = 0;
      }

      const hiddenWidth = container.scrollWidth - containerRect.width;

      /* istanbul ignore else */
      if (container.scrollLeft >= hiddenWidth) {
        this.startX = x;
        this.scrollLeft = hiddenWidth;
      }
    }
  };

  private changeControlsVisibility = () => {
    const container = this.scrollRef.current;
    const { leftArrowVisible, rightArrowVisible } = this.state;

    /* istanbul ignore else */
    if (container) {
      const containerRect = getElementRect(container);
      const leftChildRect = getElementRect(container.firstChild as HTMLLIElement);
      const rightChildRect = getElementRect(container.lastChild as HTMLLIElement);

      const leftBorderPosition = container.scrollLeft;
      const rightBorderPosition = containerRect.width + container.scrollLeft;
      const showLeftArrow = leftChildRect.left - containerRect.left < leftBorderPosition;
      const showRightArrow = rightChildRect.right - containerRect.left > rightBorderPosition;

      if (leftArrowVisible !== showLeftArrow) {
        this.setState({ leftArrowVisible: showLeftArrow });
      }

      if (rightArrowVisible !== showRightArrow) {
        this.setState({ rightArrowVisible: showRightArrow });
      }
    }
  };

  private throttledHandleScroll = throttle(SCROLL_THROTTLING_DELAY, this.changeControlsVisibility);

  private onRightArrowClick = () => {
    const container = this.scrollRef.current;

    /* istanbul ignore else */
    if (container) {
      const scrollOffset = getRightScrollOffset(container, this.props.scrollOffset || 0) as number;

      scrollToPos(container, scrollOffset, 300);
    }
  };

  private onLeftArrowClick = () => {
    const container = this.scrollRef.current;

    /* istanbul ignore else */
    if (container) {
      const scrollOffset = getLeftScrollOffset(container, this.props.scrollOffset || 0) as number;

      scrollToPos(container, scrollOffset, 300);
    }
  };
}
